/**
 * Email capture for the coming-soon page.
 *
 * Submissions are appended as rows to a Google Sheet by an Apps Script Web App.
 * See docs/google-sheets-endpoint.md for the script and deployment steps.
 */

/** Apps Script Web App URL. Empty when the deployment has not been wired up yet. */
export const NOTIFY_ENDPOINT: string = import.meta.env.VITE_NOTIFY_ENDPOINT ?? '';

export type NotifyOutcome =
  /** The row reached the sheet. */
  | 'ok'
  /** No endpoint configured — nothing was sent, and nobody should be told otherwise. */
  | 'unconfigured'
  /** The endpoint was called and did not accept the submission. */
  | 'failed';

type NotifyOptions = {
  endpoint?: string;
  fetchImpl?: typeof fetch;
};

export async function submitNotifyRequest(
  email: string,
  domain: string,
  { endpoint = NOTIFY_ENDPOINT, fetchImpl = globalThis.fetch }: NotifyOptions = {},
): Promise<NotifyOutcome> {
  if (!endpoint.trim()) {
    console.error('[nawy-coming-soon] VITE_NOTIFY_ENDPOINT is not set — the email was NOT saved. See docs/google-sheets-endpoint.md.');
    return 'unconfigured';
  }

  try {
    const response = await fetchImpl(endpoint, {
      method: 'POST',
      // Apps Script Web Apps do not answer the CORS preflight that an
      // application/json body would trigger. text/plain keeps this a "simple
      // request", so the POST goes straight through; Apps Script parses the
      // body as JSON regardless.
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify({ email: email.trim().toLowerCase(), domain }),
      redirect: 'follow',
    });

    if (!response.ok) {
      console.error(`[nawy-coming-soon] notify endpoint returned ${response.status}`);
      return 'failed';
    }

    // Apps Script answers 200 even when it rejects a submission, so the only
    // signal of a server-side failure is `ok: false` in the payload. A body
    // that isn't readable JSON leaves the 200 standing.
    if (await rejectedInBody(response)) {
      return 'failed';
    }

    return 'ok';
  } catch (error) {
    // Note: a CORS failure reading the reply also lands here, and Apps Script
    // will have written the row regardless. That reports a false negative
    // rather than a false success, which is the safer way to be wrong.
    console.error('[nawy-coming-soon] notify request failed', error);
    return 'failed';
  }
}

async function rejectedInBody(response: Response): Promise<boolean> {
  try {
    const text = await response.text();
    if (!text) return false;

    const body: unknown = JSON.parse(text);
    if (typeof body === 'object' && body !== null && (body as { ok?: unknown }).ok === false) {
      console.error('[nawy-coming-soon] notify endpoint rejected the submission', text);
      return true;
    }
  } catch {
    // Unreadable or non-JSON body — nothing to contradict the 200.
  }

  return false;
}
